/**
 * Builds the Fair Entitlements Guarantee verification spreadsheet.
 *
 * This reproduces the layout of the FEG Verification Spreadsheet
 * (version 9.4) rather than inventing one, because the department
 * imports the file directly into the FEG Branch DB:
 *
 * - Rows 1–8 carry the claim-level block: the title, the version, the
 *   employer/appointment fields down column A with their values in
 *   column B, and the practitioner contact line in columns C/D.
 * - Row 10 is the merged group heading row, row 11 the 50 column
 *   headings, row 12 a blank spacer, and the employee rows start at
 *   row 13 — where the template's own pre-filled formula rows begin.
 * - The nine columns FEG calculates for itself (L, M, Q, R, Z, AD, AH,
 *   AL, AP) are filled with our figures rather than left blank. The
 *   instruction sheet permits it, and it gives the department our
 *   working to check against.
 * - Every monetary cell is a static number to two decimal places. The
 *   instruction sheet is explicit that values must not be formulas.
 * - Dates are `DD/MM/YYYY` text with leading zeros, matching the
 *   documented example. A real Excel date cell carries a locale
 *   dependent format and a silent day/month transposition on an import
 *   we cannot re-check is the worse failure.
 *
 * `xlsx` is driven directly here rather than through `exportToExcel`,
 * which writes a single header row and cannot express merged cells or a
 * metadata block. Changing that shared helper would touch every other
 * export in the app.
 *
 * Column letters in the comments below refer to the real template, so
 * they can be checked against it directly.
 */

import * as XLSX from "xlsx";
import {
  computeFegDerived,
  digitsOnly,
  formatFegDate,
  isFegEmploymentBasis,
  roundCurrency,
  splitEmployeeName,
  DIRECTOR_RELATIONSHIP_LABELS,
  EMPLOYMENT_BASIS_LABELS,
  EMPLOYMENT_TYPE_LABELS,
  TERMINATION_TYPE_LABELS,
  type FegCaseDates,
} from "@cvx/lib/employeeEntitlements";
import type { EmployeeEntryUI } from "./types";

/** Sheet name in the customer's template. */
const SHEET_NAME = "FEG Entitlements";

/** Version of the template this builder reproduces. */
export const FEG_TEMPLATE_VERSION = "Version 9.4";

/**
 * The 50 column headings of row 11, verbatim from the template and in
 * template order (A through AX). Do not reword: the import matches on
 * these strings.
 */
const COLUMN_HEADERS = [
  "Family Name", // A
  "First Name", // B
  "Date of Birth", // C
  "Employment Type", // D
  "Relationship to Director/Owner", // E
  "Basis of Employment", // F
  "State in which employed", // G
  "Date of Commencement", // H
  "Date of Termination", // I
  "Employment Termination Type", // J
  "Date of Notification/Resignation", // K
  "Service at IP appointment", // L  (FEG-calculated)
  "Service at Termination", // M  (FEG-calculated)
  "Hourly wage rate", // N
  "Average number of hours worked per week", // O
  "Regular Weekly Allowances", // P
  "Base Weekly Wage", // Q  (FEG-calculated)
  "Total Weekly Wage", // R  (FEG-calculated)
  "Total Outstanding Wages (since commencement)", // S
  "Unpaid Wages within the 13 week wage entitlement period", // T
  "Allowances within the 13 week wage entitlement period", // U
  "Commission within the 13 week wage entitlement period", // V
  "Other wages entitlements (Rostered days off etc)", // W
  "Unpaid Wages Comments", // X
  "Annual Leave Hours at Termination", // Y
  "$ Annual leave owed", // Z  (FEG-calculated)
  "$ Winding up costs for Annual Leave", // AA
  "Annual Leave Comments", // AB
  "% Leave Loading Rate", // AC
  "Leave Loading Total", // AD (FEG-calculated)
  "$ Winding up costs for Leave Loading", // AE
  "Leave Loading Comments", // AF
  "Long Service Leave Hours at Termination", // AG
  "$ Long Service leave owed", // AH (FEG-calculated)
  "$ Winding up costs for Long Service Leave", // AI
  "Long Service Leave Comments", // AJ
  "PILN Weeks Owing", // AK
  "$ PILN owed", // AL (FEG-calculated)
  "$ Winding up costs for PILN", // AM
  "PILN Comments", // AN
  "Redundancy Weeks Owing", // AO
  "$ Redundancy owed", // AP (FEG-calculated)
  "$ Winding up costs for Redundancy", // AQ
  "Redundancy Comments", // AR
  "Unpaid Wages PAT", // AS
  "Annual Leave PAT", // AT
  "Long Service Leave PAT", // AU
  "PILN PAT", // AV
  "Redundancy PAT", // AW
  "Payments After Termination Comments", // AX
];

/**
 * Row 10 group headings, as `[label, firstColumnIndex, lastColumnIndex]`
 * zero-based against `COLUMN_HEADERS`.
 *
 * Family Name / First Name / Date of Birth (A–C) sit outside any group,
 * matching the template.
 */
const COLUMN_GROUPS: Array<[string, number, number]> = [
  ["Employment Details", 3, 7], // D–H
  ["End of Employment", 8, 12], // I–M
  ["Wage Rate", 13, 17], // N–R
  ["Unpaid Wages", 18, 23], // S–X
  ["Annual Leave", 24, 27], // Y–AB
  ["Leave Loading", 28, 31], // AC–AF
  ["Long Service Leave", 32, 35], // AG–AJ
  ["PILN", 36, 39], // AK–AN
  ["Redundancy", 40, 43], // AO–AR
  ["Payments After Termination", 44, 49], // AS–AX
];

/** Zero-based row index of the merged group heading row (row 10). */
const GROUP_HEADER_ROW = 9;

type Cell = string | number | null;

/** Monetary cells: static numbers to 2 dp, blank when unknown. */
function money(value: number | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return roundCurrency(value);
}

/** Non-monetary numerics (hours, weeks, rates) pass through unrounded. */
function num(value: number | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return value;
}

function text(value: string | undefined): string {
  return value?.trim() ?? "";
}

function label(map: Record<string, string>, key: string | undefined): string {
  if (!key) return "";
  return map[key] ?? key;
}

/**
 * Basis of employment, blank when the stored value is one FEG cannot
 * accept. Morgan's taxonomy is wider than the template's three values.
 */
function fegBasis(value: string | undefined): string {
  if (!isFegEmploymentBasis(value)) return "";
  return EMPLOYMENT_BASIS_LABELS[value];
}

/** How many entries carry a basis of employment FEG will not accept. */
export function countUnexportableBasis(entries: EmployeeEntryUI[]): number {
  return entries.filter(
    (e) => e.employmentCategory && !isFegEmploymentBasis(e.employmentCategory),
  ).length;
}

/**
 * The figure to claim for one category: the stored amount when the
 * practitioner or a document supplied one, otherwise what the captured
 * inputs calculate to.
 */
function claimed(
  stored: number | undefined,
  calculated: number | undefined,
): number | null {
  return money(stored ?? calculated);
}

/** The claim-level details FEG asks for once, in rows 3–8. */
export interface FegClaimContext {
  employerLegalName?: string;
  employerAbn?: string;
  completedBy?: string;
  administrationDate?: string;
  liquidationDate?: string;
  administrationContinuous?: boolean;
  /** Practitioner contact line, from the org's branding profile. */
  contactPhone?: string;
  contactEmail?: string;
}

function buildDataRow(entry: EmployeeEntryUI, caseDates: FegCaseDates): Cell[] {
  const derived = computeFegDerived(entry, caseDates);
  const ent = entry.entitlements ?? {};

  // Rows predating the structured split fall back to splitting the
  // display name, so the two required columns are never blank.
  const fallback = splitEmployeeName(entry.name);

  const wages = entry.wagesDetail ?? {};
  const annual = entry.annualLeaveDetail ?? {};
  const loading = entry.leaveLoadingDetail ?? {};
  const lsl = entry.longServiceLeaveDetail ?? {};
  const piln = entry.pilnDetail ?? {};
  const redundancy = entry.redundancyDetail ?? {};
  const pat = entry.paymentsAfterTermination ?? {};

  return [
    text(entry.familyName) || fallback.familyName, // A
    text(entry.firstName) || fallback.firstName, // B
    formatFegDate(entry.dateOfBirth), // C

    label(EMPLOYMENT_TYPE_LABELS, entry.employmentType), // D
    label(DIRECTOR_RELATIONSHIP_LABELS, entry.relationshipToDirector), // E
    fegBasis(entry.employmentCategory), // F
    text(entry.stateOfEmployment), // G
    formatFegDate(entry.startDate), // H

    formatFegDate(entry.terminationDate), // I
    label(TERMINATION_TYPE_LABELS, entry.terminationType), // J
    formatFegDate(entry.notificationDate), // K
    num(derived.serviceAtAppointment), // L
    num(derived.serviceAtTermination), // M

    money(entry.wageRate), // N
    num(entry.averageWeeklyHours), // O
    money(entry.regularWeeklyAllowances), // P
    money(derived.baseWeeklyWage), // Q
    money(derived.totalWeeklyWage), // R

    money(wages.totalOutstanding), // S
    money(wages.unpaidInPeriod), // T
    money(wages.allowancesInPeriod), // U
    money(wages.commissionInPeriod), // V
    money(wages.otherEntitlements), // W
    text(wages.comments), // X

    num(annual.hoursAtTermination), // Y
    claimed(ent.accruedAnnualLeave, derived.annualLeave.calculated), // Z
    money(annual.windUpCost), // AA
    text(annual.comments), // AB

    num(loading.ratePercent), // AC
    claimed(ent.leaveLoading, derived.leaveLoading.calculated), // AD
    money(loading.windUpCost), // AE
    text(loading.comments), // AF

    num(lsl.hoursAtTermination), // AG
    claimed(ent.accruedLongServiceLeave, derived.longServiceLeave.calculated), // AH
    money(lsl.windUpCost), // AI
    text(lsl.comments), // AJ

    num(piln.weeksOwing), // AK
    claimed(ent.paymentInLieuOfNotice, derived.piln.calculated), // AL
    money(piln.windUpCost), // AM
    text(piln.comments), // AN

    num(redundancy.weeksOwing), // AO
    claimed(ent.redundancyPay, derived.redundancy.calculated), // AP
    money(redundancy.windUpCost), // AQ
    text(redundancy.comments), // AR

    money(pat.unpaidWages), // AS
    money(pat.annualLeave), // AT
    money(pat.longServiceLeave), // AU
    money(pat.piln), // AV
    money(pat.redundancy), // AW
    text(pat.comments), // AX
  ];
}

/**
 * Assembles the sheet as an array of arrays so the row/column positions
 * match the template exactly, including the deliberately blank row 9.
 */
function buildAoa(
  entries: EmployeeEntryUI[],
  caseDates: FegCaseDates,
  claim: FegClaimContext,
): Cell[][] {
  const width = COLUMN_HEADERS.length;
  const blankRow = (): Cell[] => Array.from({ length: width }, () => null);

  const rows: Cell[][] = [];

  /** Writes `value` at `col` on a fresh full-width row. */
  const metaRow = (pairs: Array<[number, Cell]>): Cell[] => {
    const row = blankRow();
    for (const [col, value] of pairs) row[col] = value;
    return row;
  };

  // Row 1 — title.
  rows.push(
    metaRow([[0, "Fair Entitlements Guarantee (FEG) Entitlement Spreadsheet"]]),
  );
  // Row 2 — version.
  rows.push(metaRow([[0, FEG_TEMPLATE_VERSION]]));
  // Rows 3–8 — employer/appointment block in A/B, contact line in C/D.
  rows.push(
    metaRow([
      [0, "Employer Legal Name"],
      [1, text(claim.employerLegalName)],
      [2, "FEG Insolvency Practitioner Contact Line"],
    ]),
  );
  rows.push(
    metaRow([
      [0, "Employer ABN"],
      [1, digitsOnly(claim.employerAbn)],
      [2, "Phone"],
      [3, text(claim.contactPhone)],
    ]),
  );
  rows.push(
    metaRow([
      [0, "Completed By"],
      [1, text(claim.completedBy)],
      [2, "Email"],
      [3, text(claim.contactEmail)],
    ]),
  );
  rows.push(
    metaRow([
      [0, "Administration Date"],
      [1, formatFegDate(claim.administrationDate)],
    ]),
  );
  rows.push(
    metaRow([
      [0, "Liquidation Date"],
      [1, formatFegDate(claim.liquidationDate)],
    ]),
  );
  rows.push(
    metaRow([
      [0, "Was Administration Continuous?"],
      [
        1,
        claim.administrationContinuous === undefined
          ? ""
          : claim.administrationContinuous
            ? "Yes"
            : "No",
      ],
    ]),
  );

  // Row 9 — deliberately blank, as in the template.
  rows.push(blankRow());

  // Row 10 — group headings at the first column of each span.
  const groupRow = blankRow();
  for (const [text_, start] of COLUMN_GROUPS) groupRow[start] = text_;
  rows.push(groupRow);

  // Row 11 — column headings.
  rows.push([...COLUMN_HEADERS]);

  // Row 12 — blank. The template's own formula rows run 13 to 100, so
  // this spacer sits between the headings and the first employee.
  rows.push(blankRow());

  // Row 13 onwards — one row per employee, sorted by family then first
  // name so the sheet reads like a payroll listing.
  const sorted = [...entries].sort((a, b) => {
    const aSplit = splitEmployeeName(a.name);
    const bSplit = splitEmployeeName(b.name);
    const aFamily = (a.familyName || aSplit.familyName).toLowerCase();
    const bFamily = (b.familyName || bSplit.familyName).toLowerCase();
    if (aFamily !== bFamily) return aFamily.localeCompare(bFamily);
    const aFirst = (a.firstName || aSplit.firstName).toLowerCase();
    const bFirst = (b.firstName || bSplit.firstName).toLowerCase();
    return aFirst.localeCompare(bFirst);
  });
  for (const entry of sorted) rows.push(buildDataRow(entry, caseDates));

  return rows;
}

/**
 * Builds the FEG verification workbook for the active employees on a job.
 *
 * @param entries - Active employee entries. Merged rows should already be
 *   filtered out.
 * @param caseDates - Insolvency dates driving the service columns.
 * @param claim - The claim-level block for rows 3–8.
 */
export function buildFegWorkbook(
  entries: EmployeeEntryUI[],
  caseDates: FegCaseDates,
  claim: FegClaimContext = {},
): Blob {
  const aoa = buildAoa(entries, caseDates, claim);

  const ws = XLSX.utils.aoa_to_sheet(
    // `null` leaves the cell genuinely empty rather than writing "".
    aoa.map((row) => row.map((cell) => (cell === null ? undefined : cell))),
  );

  // Merge each group heading across its span, and the title across the
  // first few columns, so the sheet reads like the template.
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
    ...COLUMN_GROUPS.map(([, start, end]) => ({
      s: { r: GROUP_HEADER_ROW, c: start },
      e: { r: GROUP_HEADER_ROW, c: end },
    })),
  ];

  // Widths sized to the heading, which is the longest text in most
  // columns, with the comment columns given room to breathe.
  ws["!cols"] = COLUMN_HEADERS.map((header) => ({
    wch: Math.min(Math.max(header.length + 2, 12), 42),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, SHEET_NAME);

  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}
